import {
  useCatalogList,
  useSavedFilters,
  useSession,
  useSpecimenList,
  useStats,
  useStatsSummary,
} from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import { Button, Card, CardContent, CardHeader, CardTitle } from '@numistic/ui'
import { createRoute, Link } from '@tanstack/react-router'

import { formatMoney } from '../lib/format.js'
import { rootRoute } from './root.js'

export const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: HomePage,
})

function HomePage() {
  const { data: session, isPending } = useSession()
  if (isPending) return null
  return session ? <Dashboard name={session.user.name} /> : <Welcome />
}

/** Гость: что это за сайт, куда идти, последние записи каталога. */
function Welcome() {
  const { t } = useTranslation()
  const recent = useCatalogList({ sort: 'recent', limit: 10 })
  const items = recent.data?.pages[0]?.items ?? []
  return (
    <div className="grid gap-10">
      <section className="grid max-w-2xl gap-4 pt-4">
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">{t('home.title')}</h1>
        <p className="text-lg text-muted-foreground">{t('home.lead')}</p>
        <p className="text-sm text-muted-foreground">{t('home.guest')}</p>
        <div className="flex flex-wrap gap-3">
          <Button asChild>
            <Link to="/catalog" search={{}}>
              {t('home.openCatalog')}
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/login">{t('nav.signIn')}</Link>
          </Button>
        </div>
      </section>
      {items.length ? (
        <section className="grid gap-3">
          <h2 className="text-lg font-semibold">{t('home.recentCatalog')}</h2>
          <Strip
            items={items.map((i) => ({
              id: i.id,
              title: i.title,
              thumb: i.thumb,
              link: { to: '/catalog/$id' as const, id: i.id },
            }))}
          />
        </section>
      ) : null}
    </div>
  )
}

/** Вошедший: быстрые действия, деньги, недавние экземпляры, разрезы и умные коллекции. */
function Dashboard({ name }: { name: string }) {
  const { t } = useTranslation()
  const byType = useStats('type')
  const summary = useStatsSummary()
  const views = useSavedFilters()
  const recent = useSpecimenList({ sort: 'recent', limit: 8 })
  const total = (byType.data ?? []).reduce((sum, r) => sum + r.count, 0)
  const recentItems = recent.data?.pages[0]?.items ?? []
  const s = summary.data

  return (
    <div className="grid gap-8">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="mr-auto text-2xl font-semibold">{t('home.welcome', { name })}</h1>
        <Button asChild>
          <Link to="/s/new" search={{}}>
            {t('collections.addSpecimen')}
          </Link>
        </Button>
        <Button asChild variant="outline">
          <Link to="/catalog" search={{}}>
            {t('home.findInCatalog')}
          </Link>
        </Button>
      </div>

      {byType.isSuccess && total === 0 ? <Onboarding /> : null}

      {s && total > 0 ? (
        <div className="grid gap-4 sm:grid-cols-3">
          <Metric label={t('stats.specimensOwned')} value={String(s.owned)} to="/c" />
          <Metric
            label={t('stats.spent')}
            value={formatMoney(s.spent, s.baseCurrency)}
            to="/stats"
          />
          <Metric
            label={t('stats.value')}
            value={formatMoney(s.value, s.baseCurrency)}
            to="/stats"
          />
        </div>
      ) : null}

      {recentItems.length ? (
        <section className="grid gap-3">
          <div className="flex items-baseline gap-3">
            <h2 className="mr-auto text-lg font-semibold">{t('home.recentSpecimens')}</h2>
            <Link
              to="/c"
              search={{}}
              className="text-sm text-primary underline underline-offset-4 hover:decoration-2"
            >
              {t('home.allSpecimens')}
            </Link>
          </div>
          <Strip
            items={recentItems.map((i) => ({
              id: i.id,
              title: i.title,
              thumb: i.thumb,
              link: { to: '/s/$id' as const, id: i.id },
            }))}
          />
        </section>
      ) : null}

      {total > 0 ? (
        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('stats.byType')}</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="grid gap-1.5" aria-label={t('stats.byType')}>
                {(byType.data ?? []).map((r) => (
                  <li key={r.key ?? r.label} className="flex justify-between text-sm">
                    <span>{r.label}</span>
                    <span className="font-medium tabular-nums">{r.count}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('collections.views')}</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="grid gap-1.5 text-sm">
                <li>
                  <Link to="/wanted" className="hover:text-primary">
                    {t('collections.wanted')}
                  </Link>
                </li>
                <li>
                  <Link to="/for-sale" className="hover:text-primary">
                    {t('collections.forSale')}
                  </Link>
                </li>
                {(views.data ?? []).map((v) => (
                  <li key={v.id}>
                    <Link to="/views/$id" params={{ id: v.id }} className="hover:text-primary">
                      {v.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      ) : null}
    </div>
  )
}

function Metric({ label, value, to }: { label: string; value: string; to: '/c' | '/stats' }) {
  return (
    <Link
      to={to}
      search={{}}
      className="grid gap-1 rounded-lg border bg-card p-4 transition-colors hover:border-primary/60"
    >
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-2xl font-semibold tabular-nums">{value}</span>
    </Link>
  )
}

/** Первые шаги для пустой коллекции. */
function Onboarding() {
  const { t } = useTranslation()
  const steps = [
    {
      title: t('home.steps.collections'),
      text: t('home.steps.collectionsText'),
      to: '/c' as const,
    },
    { title: t('home.steps.add'), text: t('home.steps.addText'), to: '/s/new' as const },
    {
      title: t('home.steps.numista'),
      text: t('home.steps.numistaText'),
      to: '/catalog/import/numista' as const,
    },
  ]
  return (
    <section className="grid gap-3" aria-labelledby="onboarding-title">
      <h2 id="onboarding-title" className="text-lg font-semibold">
        {t('home.steps.title')}
      </h2>
      <ol className="grid gap-4 md:grid-cols-3">
        {steps.map((step, i) => (
          <li key={step.to}>
            <Link
              to={step.to}
              search={{}}
              className="grid h-full gap-2 rounded-lg border bg-card p-4 transition-colors hover:border-primary/60"
            >
              <span className="text-sm text-muted-foreground">{i + 1}</span>
              <span className="font-medium">{step.title}</span>
              <span className="text-sm text-muted-foreground">{step.text}</span>
            </Link>
          </li>
        ))}
      </ol>
    </section>
  )
}

/** Лента миниатюр: горизонтальная прокрутка на телефоне, сетка на широком экране. */
function Strip({
  items,
}: {
  items: Array<{
    id: string
    title: string
    thumb: string | null
    link: { to: '/catalog/$id' | '/s/$id'; id: string }
  }>
}) {
  const { t } = useTranslation()
  return (
    <ul className="-mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-2 [scrollbar-width:none] sm:mx-0 sm:grid sm:grid-cols-4 sm:overflow-visible sm:px-0 lg:grid-cols-5">
      {items.map((item) => (
        <li key={item.id} className="w-36 shrink-0 snap-start sm:w-auto">
          <Link
            to={item.link.to}
            params={{ id: item.link.id }}
            className="group grid gap-2 rounded-lg border bg-card p-2 hover:border-primary/60"
          >
            <div className="flex aspect-square items-center justify-center overflow-hidden rounded-md bg-muted">
              {item.thumb ? (
                <img src={item.thumb} alt="" loading="lazy" className="size-full object-contain" />
              ) : (
                <span className="text-xs text-muted-foreground">{t('catalog.noImage')}</span>
              )}
            </div>
            <span className="line-clamp-2 px-1 pb-1 text-sm font-medium group-hover:text-primary">
              {item.title}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  )
}
